import React, { useCallback, useRef, useEffect } from 'react';
import { getConfig } from '@edx/frontend-platform';

import { useLanguageSwitcher } from '../../hooks';

import './index.scss';
import ArrowMenuDrop from '../../img/icons/ArrowMenuDrop';

interface LanguageSwitcherProps {
  isMobile: boolean;
  handleCloseMenus: () => void;
  setSubmenuOverlayVisible: (flag: boolean) => void;
}

interface SiteLanguages {
  code: string;
  name: string;
  label: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  isMobile,
  handleCloseMenus,
  setSubmenuOverlayVisible,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const aboutDropDownRef = useRef<HTMLDivElement>(null);

  const { handleSelectLanguage, locale } = useLanguageSwitcher();
  const siteLanguages: SiteLanguages[] = getConfig().SITE_LANGUAGES || [];
  const currentLanguage: string = siteLanguages.find(
    ({ code }: SiteLanguages) => code === locale,
  )?.label || locale;

  const handleOpen = useCallback((el: React.RefObject<HTMLDivElement>) => {
    if (isMobile) {
      handleCloseMenus();
      el.current?.classList.toggle('active');
    }
  }, [isMobile, handleCloseMenus]);

  const handleChange = useCallback((code: string, el: React.RefObject<HTMLDivElement>) => {
    handleSelectLanguage(code);
    el.current?.classList.remove('active');
    setSubmenuOverlayVisible(false);
  }, [handleSelectLanguage, setSubmenuOverlayVisible]);

  const handleMouseEnter = (el: React.RefObject<HTMLDivElement>) => {
    if (!isMobile) {
      el.current?.classList.add('active');
      setSubmenuOverlayVisible(true);
    }
  };

  const handleMouseLeave = (el: React.RefObject<HTMLDivElement>) => {
    if (!isMobile) {
      el.current?.classList.remove('active');
      setSubmenuOverlayVisible(false);
    }
  };

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches;
      if (ref.current && !ref.current.contains(event.target as Node) && isMobileViewport) {
        aboutDropDownRef.current?.classList.remove('active');
        setSubmenuOverlayVisible(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };
  }, [setSubmenuOverlayVisible]);

  return (
    siteLanguages.length ? (
      <div className="language-switcher" ref={ref}>
        <div // eslint-disable-line
          className="w-dropdown"
          data-name="about"
          onMouseEnter={() => handleMouseEnter(aboutDropDownRef)}
          onMouseLeave={() => handleMouseLeave(aboutDropDownRef)}
          onClick={() => handleOpen(aboutDropDownRef)}
        >
          {currentLanguage}
          <ArrowMenuDrop />
          <div
            className="drop-down about-dd"
            data-name="about"
            ref={aboutDropDownRef}
          >
            <div className="about-drop">
              {siteLanguages.map(({ code, name }) => (
                <li key={code}>
                  <button
                    className="language-switcher-dropdown-item"
                    type="button"
                    onClick={() => handleChange(code, aboutDropDownRef)}
                    aria-label={name}
                  >
                    {name}
                  </button>
                </li>
              ))}
            </div>
          </div>
        </div>
      </div>
    ) : null
  );
};

export default LanguageSwitcher;
